import { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { Plus, FileText } from "lucide-react";
import { api, errMsg, fmtDate, fmtTime, fmtMoney, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stamp, EmptyState, StatCard } from "@/components/common";
import { FormDialog } from "@/components/forms";
import PunchClock from "@/components/PunchClock";
import { AttachmentList, DownloadButton } from "@/components/files";
import { LeaveTab, WfhTab, BonusTab, SlotChip } from "@/pages/MyPolicyTabs";
import { PayslipBreakdown, periodBS } from "@/components/PayslipBreakdown";
import { MyInsuranceTab } from "@/pages/Insurance";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TABS = [["attendance", "Attendance"], ["leave", "Leave"], ["wfh", "Work From Home"], ["overtime", "Overtime"], ["reimbursement", "Reimbursement"], ["salary_advance", "Salary Advance"], ["payslip", "Payslip"], ["bonus", "Bonus & Incentive"], ["insurance", "Insurance"], ["contract_history", "Contract History"], ["job_history", "Job History"], ["resignation", "Resignation"]];

const REQUEST_FIELDS = {
  leave: [{ name: "category", label: "Type", type: "select", options: ["Annual", "Sick", "Personal", "Unpaid"], default: "Annual" }, { name: "title", label: "Title", required: true, placeholder: "Family trip" }, { name: "start_date", label: "From", type: "date", required: true }, { name: "end_date", label: "To", type: "date", required: true }, { name: "reason", label: "Reason", type: "textarea" }],
  overtime: [{ name: "date", label: "Date", type: "date", required: true }, { name: "hours", label: "Hours", type: "number", required: true }, { name: "title", label: "Title", required: true, full: true, placeholder: "Release weekend" }, { name: "reason", label: "Reason", type: "textarea" }],
  reimbursement: [{ name: "category", label: "Category", type: "select", options: ["Travel", "Meals", "Software", "Equipment", "Other"], default: "Travel" }, { name: "amount", label: "Amount", type: "number", required: true }, { name: "title", label: "Title", required: true, full: true }, { name: "reason", label: "Details", type: "textarea" }, { name: "attachments", label: "Receipts", type: "file" }],
  salary_advance: [{ name: "amount", label: "Amount", type: "number", required: true }, { name: "repayment_months", label: "Repay over (months)", type: "number", default: 3 }, { name: "title", label: "Title", required: true, full: true, default: "Salary advance" }, { name: "reason", label: "Reason", type: "textarea" }],
  resignation: [{ name: "last_working_day", label: "Last working day", type: "date", required: true, full: true }, { name: "title", label: "Title", full: true, default: "Resignation" }, { name: "reason", label: "Reason", type: "textarea", required: true }],
  attendance_correction: [{ name: "date", label: "Date", type: "date", required: true, full: true }, { name: "check_in", label: "Correct check-in", type: "datetime-local" }, { name: "check_out", label: "Correct check-out", type: "datetime-local" }, { name: "title", label: "Title", full: true, default: "Attendance correction" }, { name: "reason", label: "Reason", type: "textarea" }],
};

export default function MyHR() {
  const { tab = "attendance" } = useParams();
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="My HR · Self service" title="My ledger" description="Your own attendance, requests, payslips and history. Everything here is yours alone." />
      <div className="flex gap-1 overflow-x-auto border-b border-border" data-testid="myhr-tabs">
        {TABS.map(([k, l]) => <NavLink key={k} to={`/my/${k}`} data-testid={`myhr-tab-${k}`} className={({ isActive }) => cn("px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors", isActive ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}>{l}</NavLink>)}
      </div>
      {tab === "attendance" ? <AttendanceTab /> : tab === "payslip" ? <PayslipTab /> : tab === "leave" ? <LeaveTab /> : tab === "wfh" ? <WfhTab /> : tab === "bonus" ? <BonusTab /> : tab === "insurance" ? <MyInsuranceTab /> : tab === "contract_history" || tab === "job_history" ? <HistoryTab kind={tab} /> : <RequestsTab type={tab} />}
    </div>
  );
}

function AttendanceTab() {
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/attendance/me").then((r) => setRows(r.data)); api.get("/attendance/today").then((r) => setToday(r.data)); };
  useEffect(load, []);
  const totalHours = rows.reduce((a, r) => a + (r.hours || 0), 0).toFixed(1);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
        {today && <PunchClock initial={today} onChange={load} />}
        <StatCard label="Days logged" value={rows.length} hint="last 31 entries" testId="stat-days-logged" />
        <StatCard label="Hours" value={totalHours} hint="total across entries" accent="text-sage" testId="stat-hours" />
      </div>
      <div className="paper-card overflow-hidden rise">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
          <span className="font-serif font-medium">Attendance log</span>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="request-correction-button" onClick={() => setOpen(true)}>Request correction</Button>
        </div>
        {rows.length === 0 ? <EmptyState title="No attendance yet" hint="Punch in to start your log." /> : rows.map((r) => (
          <div key={r.id} className="ledger-row" style={{ gridTemplateColumns: "1.1fr auto 1fr 1fr 0.8fr auto" }} data-testid={`attendance-row-${r.date}`}>
            <div className="font-medium text-sm">{fmtDate(r.date)}</div>
            <SlotChip slot={r.slot} time={r.slot_time} />
            <div className="font-mono text-xs text-muted-foreground">in {fmtTime(r.check_in)}{r.late_minutes > 0 && <span className="text-amber ml-1">+{r.late_minutes}m</span>}</div>
            <div className="font-mono text-xs text-muted-foreground">out {fmtTime(r.check_out)}{r.completion_label && <span className="ml-1 text-foreground">· done {r.completion_label}</span>}</div>
            <div className="font-mono text-xs">{r.hours ? `${r.hours}h` : "—"}</div>
            <Stamp status={r.status} />
          </div>
        ))}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title="Attendance correction" fields={REQUEST_FIELDS.attendance_correction} initial={{ title: "Attendance correction" }} submitLabel="Submit request" testId="correction-dialog"
        onSubmit={async (f) => { await api.post("/requests", { ...f, type: "attendance_correction", check_in: f.check_in ? new Date(f.check_in).toISOString() : undefined, check_out: f.check_out ? new Date(f.check_out).toISOString() : undefined }); toast.success("Correction requested"); }} />
    </div>
  );
}

function RequestsTab({ type }) {
  const [rows, setRows] = useState([]);
  const [bal, setBal] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/requests/me", { params: { type } }).then((r) => setRows(r.data)); if (type === "leave") api.get("/leave/balance").then((r) => setBal(r.data)); };
  useEffect(load, [type]); // eslint-disable-line
  const fields = REQUEST_FIELDS[type] || [];
  const money = ["reimbursement", "salary_advance"].includes(type);
  return (
    <div className="space-y-5">
      {type === "leave" && bal && (
        <div className="grid grid-cols-3 gap-5 stagger">
          <StatCard label="Entitlement" value={`${bal.total}d`} testId="leave-total" />
          <StatCard label="Used" value={`${bal.used}d`} accent="text-amber" testId="leave-used" />
          <StatCard label="Remaining" value={`${bal.remaining}d`} accent="text-sage" testId="leave-remaining" />
        </div>
      )}
      <div className="paper-card overflow-hidden rise" data-testid={`my-requests-${type}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
          <span className="font-serif font-medium">{titleCase(type)} requests</span>
          <Button size="sm" className="rounded-full" data-testid="new-request-button" onClick={() => setOpen(true)}><Plus size={14} className="mr-1" /> New request</Button>
        </div>
        {rows.length === 0 ? <EmptyState title={`No ${titleCase(type).toLowerCase()} requests`} hint="File one and it lands in your approver's inbox." /> : rows.map((r) => (
          <div key={r.id} className="ledger-row" style={{ gridTemplateColumns: "minmax(0,2fr) minmax(0,1.2fr) minmax(0,1fr) auto" }} data-testid={`request-row-${r.id}`}>
            <div><div className="font-medium text-sm">{r.title || titleCase(r.type)}</div><div className="text-xs text-muted-foreground truncate">{r.reason}</div>{r.attachments?.length > 0 && <div className="mt-1"><AttachmentList files={r.attachments} /></div>}</div>
            <div className="text-xs text-muted-foreground font-mono">{r.start_date ? `${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}` : r.date ? fmtDate(r.date) : r.last_working_day ? `Last day ${fmtDate(r.last_working_day)}` : fmtDate(r.created_at)}</div>
            <div className="text-sm font-mono">{money ? fmtMoney(r.amount) : r.days ? `${r.days}d` : r.hours ? `${r.hours}h` : ""}</div>
            <div className="flex flex-col items-end gap-1"><Stamp status={r.status} />{r.decision_comment && <span className="text-[10px] text-muted-foreground italic max-w-[160px] truncate">“{r.decision_comment}”</span>}</div>
          </div>
        ))}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={`New ${titleCase(type).toLowerCase()} request`} fields={fields} submitLabel="Submit request" testId="request-dialog"
        initial={Object.fromEntries(fields.filter((f) => f.default !== undefined).map((f) => [f.name, f.default]))}
        onSubmit={async (f) => { try { await api.post("/requests", { ...f, type }); toast.success("Request submitted"); load(); } catch (e) { throw e; } }} />
    </div>
  );
}

function PayslipTab() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/payslips/me").then((r) => setRows(r.data)); }, []);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {rows.map((p) => (
          <button key={p.id} data-testid={`payslip-card-${p.period}`} onClick={() => setSel(p)} className="paper-card paper-card-hover p-5 text-left">
            <div className="flex items-start justify-between"><div className="eyebrow">{p.period}</div><Stamp status={p.status} /></div>
            <div className="mt-3 font-serif text-3xl font-medium ticker">{fmtMoney(p.net)}</div>
            <div className="text-xs text-muted-foreground mt-1">net pay · <FileText size={11} className="inline" /> view slip</div>
          </button>
        ))}
        {rows.length === 0 && <div className="col-span-full"><EmptyState title="No payslips yet" /></div>}
      </div>
      <PayslipDialog p={sel} onClose={() => setSel(null)} />
    </div>
  );
}

export function PayslipDialog({ p, onClose }) {
  if (!p) return null;
  return (
    <Dialog open={!!p} onOpenChange={onClose}>
      <DialogContent data-testid="payslip-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Payslip · {p.period} <span className="text-base text-muted-foreground font-normal">· {periodBS(p.period)}</span></DialogTitle><DialogDescription>{p.user_name} · {p.department}</DialogDescription></DialogHeader>
        <PayslipBreakdown p={p} />
        <div className="mt-3 flex justify-between items-center"><DownloadButton path={`/payslips/${p.id}/pdf`} filename={`payslip-${p.period}.pdf`} testId="payslip-download-button" /><Stamp status={p.status} animate /></div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryTab({ kind }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/history/me").then((r) => setH(r.data)); }, []);
  if (!h) return null;
  const items = kind === "contract_history" ? h.contracts : h.jobs;
  return (
    <div className="paper-card p-6 rise" data-testid={`${kind}-timeline`}>
      <h3 className="font-serif text-xl font-medium mb-5">{kind === "contract_history" ? "Contracts" : "Positions held"}</h3>
      <ol className="relative border-l border-dashed border-border ml-2 space-y-6">
        {items.map((c, i) => (
          <li key={i} className="ml-5">
            <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-primary ring-4 ring-background" />
            <div className="font-medium">{c.type || c.title}</div>
            <div className="text-sm text-muted-foreground">{c.department}</div>
            <div className="font-mono text-xs text-muted-foreground mt-0.5">{fmtDate(c.start || c.from)} → {c.end || c.to ? fmtDate(c.end || c.to) : "present"}</div>
            {c.status && <div className="mt-1"><Stamp status={c.status} /></div>}
          </li>
        ))}
        {items.length === 0 && <li className="ml-5 text-sm text-muted-foreground">No history recorded.</li>}
      </ol>
    </div>
  );
}
